import { createFileRoute } from '@tanstack/react-router';

import {
    AgencyGuideTourReplayButton,
    AgencyGuideTourScope,
} from '@apps/web/components/page_tour/agency_guide_tour';
import { AgencyContextCard } from '@apps/web/components/settings/agency_context_card';
import { useAgencyContextDocument } from '@apps/web/components/settings/agency_context_data';
import {
    SettingsLoadingState,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

export const Route = createFileRoute('/_app/settings/agency-context')({
    component: AgencyContextSettingsPage,
});

function AgencyContextSettingsPage() {
    const { organizationId, isLoading } = useSettingsGates();
    const {
        document,
        loadFailed,
        isLoading: isDocumentLoading,
        isSaving,
        saveFailed,
        conflictCount,
        save,
    } = useAgencyContextDocument(organizationId);
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings-agency-guide-page',
    });
    // The client texting agent reads this guide only while it is on.
    const clientAgentAllowed =
        organization?.features?.client_agent_allowed === true;

    if (isLoading) {
        return <SettingsLoadingState />;
    }
    if (organizationId === null) {
        return null;
    }
    return (
        <AgencyGuideTourScope
            canEdit={document?.can_edit ?? false}
            ready={!isDocumentLoading && !loadFailed && document !== undefined}
        >
            <SettingsPageShell
                title="Agency Guide"
                description={
                    clientAgentAllowed
                        ? 'Tell Phoebe how your agency works. Phoebe uses this guide to answer questions on calls, texts, and chat, including texts with clients and their families.'
                        : 'Tell Phoebe how your agency works. Phoebe uses this guide to answer questions on calls, texts, and chat.'
                }
                headerAction={<AgencyGuideTourReplayButton />}
            >
                {/* Loading, failed, and loaded are three different states. */}
                {isDocumentLoading ? (
                    <SettingsLoadingState />
                ) : loadFailed || document === undefined ? (
                    <p className="text-muted-foreground text-sm">
                        Couldn’t load your agency guide. Phoebe is still using
                        whatever was saved last — nothing has changed.
                    </p>
                ) : (
                    <AgencyContextCard
                        // Remount on a conflict so the editor reseeds onto
                        // the winning document rather than holding a draft
                        // nobody's version matches.
                        key={`${organizationId}:${conflictCount}`}
                        document={document}
                        isSaving={isSaving}
                        saveFailed={saveFailed}
                        hadConflict={conflictCount > 0}
                        onSave={save}
                    />
                )}
            </SettingsPageShell>
        </AgencyGuideTourScope>
    );
}
